// @ts-nocheck
"use client";

import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  memo,
  use,
} from "react";
import { useParams } from "next/navigation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart3,
  Users,
  MessageSquare,
  PlusCircle,
  Loader2,
  ExternalLink,
  Clock,
  Bookmark,
  Heart,
  Share2,
  Activity,
  Eye,
  CalendarClock,
  DollarSign,
  MoreVertical,
  UserPlus,
  StopCircle,
  RefreshCw,
  XCircle,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  FileText,
  Frown,
  PlayCircle,
  Check,
  FileX,
  InfoIcon,
  Plus,
  UserMinus,
  ChevronRight,
  Share,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CampaignService } from "@/services/campaign-service";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import Image from "next/image";
import { FaTiktok } from "react-icons/fa";
import { Instagram, Youtube } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import echo from "@/lib/echo";
import { formatDistanceToNow } from "date-fns";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { AnimatedValue } from "@/components/ui/animated-value";
import { InviteCreatorsDialog } from "@/components/campaigns/invite-creators-dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Editor } from "@/components/editor";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { allTimezones } from "react-timezone-select";
import { timezones } from "@/lib/timezones";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from "@/components/ui/select";
import { countries } from "countries-list";
import { nanoid } from "nanoid";
import { Switch } from "@/components/ui/switch";
import { format, parseISO } from "date-fns";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Add this validation function at the top level
const isValidSocialMediaUrl = (url: string): boolean => {
  // Remove https://, http://, www. and query parameters
  const cleanUrl = url.replace(/^(https?:\/\/)?(www\.)?/, "").split("?")[0];

  const patterns = [
    /^tiktok\.com\/@[\w.-]+\/video\/\d+$/, // TikTok
    /^instagram\.com\/reel\/[\w-]+\/?$/, // Instagram Reel (with optional trailing slash)
    /^youtube\.com\/shorts\/[\w-]+$/, // YouTube Short
  ];

  return patterns.some((pattern) => pattern.test(cleanUrl));
};

// Add this type for creator info
type Creator = {
  uuid: string;
  name: string;
  handle: string | null;
  avatar: string;
} | null;

// Update Post interface to include creator
interface Post {
  uuid: string;
  platform: string;
  platform_id: string;
  title: string;
  caption: string;
  link: string;
  length: number;
  status: string;
  views: number;
  engagement_rate: string;
  likes: number;
  comments: number;
  shares: number;
  bookmarks: number;
  posted_at: string;
  creator: Creator;
  account: {
    name: string;
    handle: string;
    avatar: string;
  };
  is_manual: boolean;
  is_syncing: boolean;
  can_sync: boolean;
  earnings: number | null;
  synced_at: string | null;
  isNew?: boolean;
}

// Update the formatViews helper function
const formatViews = (views: number) => {
  if (views >= 1000000) {
    return `${(views / 1000000).toFixed(3).replace(/\.?0+$/, "")}M`;
  }
  if (views >= 1000) {
    return `${(views / 1000).toFixed(1)}K`;
  }
  return views.toString();
};

// Compact TimeAgo component
const TimeAgo = ({ date }: { date: string }) => {
  const [timeAgo, setTimeAgo] = useState(() =>
    formatDistanceToNow(new Date(date), { addSuffix: true })
  );
  useEffect(() => {
    const timer = setInterval(
      () =>
        setTimeAgo(formatDistanceToNow(new Date(date), { addSuffix: true })),
      60000
    );
    return () => clearInterval(timer);
  }, [date]);
  return <span>{timeAgo}</span>;
};

// Update the RecentPosts component
const RecentPosts = ({ posts }: { posts: Post[] }) => {
  const recentPosts = [...posts]
    .sort(
      (a, b) =>
        new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime()
    )
    .slice(0, 4);

  return (
    <Card className="col-span-3">
      <CardHeader>
        <CardTitle>Recent Posts</CardTitle>
      </CardHeader>
      <CardContent className="min-h-[300px]">
        {recentPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[300px] p-8 text-center">
            <Frown className="h-8 w-8 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Recent Posts</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Recent posts from the campaign will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {recentPosts.map((post) => (
              <div key={post.uuid} className="flex items-start gap-4">
                <div className="relative h-16 w-16 rounded-md overflow-hidden flex-shrink-0">
                  <Image
                    src={post.thumbnail}
                    alt={post.caption}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {post.account ? (
                          <div className="flex items-center gap-2">
                            {post.account.avatar ? (
                              <div className="relative h-5 w-5 rounded-full overflow-hidden">
                                <Image
                                  src={post.account.avatar}
                                  alt={post.account.name}
                                  fill
                                  className="object-cover"
                                />
                              </div>
                            ) : (
                              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center">
                                <span className="text-xs font-medium text-muted-foreground">
                                  {getInitial(post.account.name)}
                                </span>
                              </div>
                            )}
                            <span className="text-sm font-medium">
                              {post.account.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            Added manually
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          <TimeAgo date={post.posted_at} />
                        </span>
                      </div>
                      <p className="text-sm line-clamp-1">{post.caption}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        {post.platform === "tiktok" ? (
                          <FaTiktok className="h-4 w-4" />
                        ) : post.platform === "instagram" ? (
                          <Instagram className="h-4 w-4 text-pink-600" />
                        ) : (
                          <Youtube className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                      <a
                        href={post.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-8 w-8"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {post.views.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Activity className="h-3 w-3" />
                      {parseFloat(post.engagement_rate).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Add this helper function at the top of the file
const getAccountUrl = (platform: string, handle: string) => {
  switch (platform) {
    case "tiktok":
      return `https://tiktok.com/@${handle}`;
    case "instagram":
      return `https://instagram.com/${handle}`;
    case "youtube":
      return `https://youtube.com/@${handle}`;
    default:
      return "#";
  }
};

// Update the Campaign interface
interface Campaign {
  name: string | undefined;
  status: string;
  starts_at: string | number | Date;
  ends_at: string | number | Date;
  ended_at: string | number | Date;
  // ... existing fields ...
  budget: number | null;
  used_budget: number;
  budget_type: string;
  base_cpm: number;
  sync:
    | {
        total: number;
        completed: number;
      }
    | false;
  last_synced_at: string | null;
  invite_code: string;
  is_admin: boolean;
  description: string;
  creator_instructions: string;
  assets_link?: string;
  platforms: string[];
  creators: Creator[];
  invitations: Array<{
    id: number;
    uuid: string;
    email: string;
    user: {
      name: string;
      avatar: string;
      email: string;
    } | null;
    invited_by: {
      name: string;
      avatar: string;
    };
  }>;
}

// Update the CircularProgress component to be a badge
const SyncProgress = ({ value }: { value: number }) => {
  const [pulse, setPulse] = useState(false);
  const prevValue = useRef(value);

  useEffect(() => {
    if (value !== prevValue.current) {
      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 300);
      prevValue.current = value;
      return () => clearTimeout(timer);
    }
  }, [value]);

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium bg-background transition-transform duration-300",
        pulse && "scale-105"
      )}
    >
      <RefreshCw className="h-3 w-3 animate-spin" />
      <span>Syncing</span>
      <span className="font-bold">{Math.round(value)}%</span>
    </div>
  );
};

// Add this helper function to check if campaign can be synced
const canSync = (lastSyncedAt: string | null, status: string): boolean => {
  if (status === "pending") return false;
  if (!lastSyncedAt) return true;
  const lastSync = new Date(lastSyncedAt);
  const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
  return lastSync < thirtyMinutesAgo;
};

// Add this helper function to get next available sync time
const getNextSyncTime = (lastSyncedAt: string | null): string | null => {
  if (!lastSyncedAt) return null;
  const lastSync = new Date(lastSyncedAt);
  const nextSync = new Date(lastSync.getTime() + 30 * 60 * 1000); // 30 minutes after last sync
  return formatDistanceToNow(nextSync, { addSuffix: true });
};

// Extract PostsTable into a separate memoized component
const PostsTable = memo(
  ({
    posts,
    sortColumn,
    sortDirection,
    onSortChange,
    lastUpdatedPost,
    onRemovePost,
    onSyncPost,
    isAdmin,
  }: {
    posts: Post[];
    sortColumn: string;
    sortDirection: "asc" | "desc";
    onSortChange: (column: string) => void;
    lastUpdatedPost: string | null;
    onRemovePost: (uuid: string) => void;
    onSyncPost: (uuid: string) => void;
    isAdmin: boolean;
  }) => {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Post</TableHead>
            <TableHead
              className="w-[200px] cursor-pointer hover:bg-muted/50"
              onClick={() => onSortChange("account")}
            >
              <div className="flex items-center gap-2">
                Account
                {sortColumn === "account" && sortDirection === "asc" ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : sortColumn === "account" && sortDirection === "desc" ? (
                  <ChevronDown className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4 ml-2" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSortChange("views")}
            >
              <div className="flex items-center gap-2">
                Views
                {sortColumn === "views" && sortDirection === "asc" ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : sortColumn === "views" && sortDirection === "desc" ? (
                  <ChevronDown className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4 ml-2" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSortChange("engagement_rate")}
            >
              <div className="flex items-center gap-2">
                Engagement
                {sortColumn === "engagement_rate" && sortDirection === "asc" ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : sortColumn === "engagement_rate" &&
                  sortDirection === "desc" ? (
                  <ChevronDown className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4 ml-2" />
                )}
              </div>
            </TableHead>
            <TableHead>Interactions</TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSortChange("posted_at")}
            >
              <div className="flex items-center gap-2">
                Posted
                {sortColumn === "posted_at" && sortDirection === "asc" ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : sortColumn === "posted_at" && sortDirection === "desc" ? (
                  <ChevronDown className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4 ml-2" />
                )}
              </div>
            </TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.map((post) => (
            <TableRow
              key={post.uuid}
              className={cn(
                "border-b last:border-b-0 hover:bg-muted/50 transition-all duration-500 relative",
                post.is_syncing && "opacity-60 bg-muted/50",
                lastUpdatedPost === post.uuid &&
                  "bg-blue-50/50 dark:bg-blue-950/10",
                post.isNew &&
                  "animate-in fade-in slide-in-from-top-5 duration-1000"
              )}
            >
              <TableCell className="relative">
                <div className="flex items-center gap-3">
                  <div className="relative h-[72px] w-[128px] rounded-md overflow-hidden flex-shrink-0">
                    <Image
                      src={post.thumbnail}
                      alt={post.title || post.caption}
                      fill
                      className="object-cover"
                    />
                    {post.length && (
                      <div className="absolute bottom-1 right-1 bg-black/75 px-1.5 py-0.5 text-[10px] text-white flex items-center gap-0.5 rounded">
                        <Clock className="h-3 w-3" />
                        {Math.floor(post.length / 60)}:
                        {(post.length % 60).toString().padStart(2, "0")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium line-clamp-1 text-sm">
                        {post.caption || "No caption"}
                      </span>
                      <a
                        href={post.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary shrink-0"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    <div className="inline-flex w-fit rounded-md bg-muted/80 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {post.uuid}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {post.account.avatar ? (
                    <div className="relative h-8 w-8 rounded-full overflow-hidden">
                      <Image
                        src={post.account.avatar}
                        alt={post.account.name}
                        fill
                        className="object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                      <span className="text-sm font-medium text-muted-foreground">
                        {getInitial(post.account.name)}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-1.5">
                      {post.platform === "tiktok" ? (
                        <FaTiktok className="h-3.5 w-3.5 flex-shrink-0" />
                      ) : post.platform === "instagram" ? (
                        <Instagram className="h-3.5 w-3.5 flex-shrink-0 text-pink-600" />
                      ) : (
                        <Youtube className="h-3.5 w-3.5 flex-shrink-0 text-red-600" />
                      )}
                      <span className="font-medium text-sm truncate max-w-[150px]">
                        {post.account.name}
                      </span>
                      <a
                        href={getAccountUrl(post.platform, post.account.handle)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-6 w-6 flex-shrink-0"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                      @{post.account.handle}
                    </span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <AnimatedValue
                  value={post.views}
                  postUuid={post.uuid}
                  field="views"
                />
              </TableCell>
              <TableCell>
                <div
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${
                    parseFloat(post.engagement_rate) >= 5
                      ? "bg-green-100 text-green-800 border-green-200"
                      : parseFloat(post.engagement_rate) >= 2
                      ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                      : "bg-red-100 text-red-800 border-red-200"
                  }`}
                >
                  <Activity className="h-3 w-3 mr-1" />
                  {parseFloat(post.engagement_rate).toFixed(2)}%
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-sm">
                    <Heart className="h-3.5 w-3.5 text-muted-foreground" />
                    <AnimatedValue
                      value={post.likes}
                      postUuid={post.uuid}
                      field="likes"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      <AnimatedValue
                        value={post.comments}
                        postUuid={post.uuid}
                        field="comments"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Share2 className="h-3 w-3" />
                      <AnimatedValue
                        value={post.shares}
                        postUuid={post.uuid}
                        field="shares"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Bookmark className="h-3 w-3" />
                      <AnimatedValue
                        value={post.bookmarks}
                        postUuid={post.uuid}
                        field="bookmarks"
                      />
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="text-sm">
                    {post.posted_at
                      ? new Date(post.posted_at).toLocaleDateString()
                      : "Not posted"}
                  </span>
                  {post.posted_at && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(post.posted_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell></TableCell>
              {post.is_syncing && (
                <TableCell className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[1px] z-10">
                  <div className="flex items-center gap-2 rounded-full bg-background border px-3 py-1.5 shadow-sm">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span className="text-sm font-medium">Syncing post...</span>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if these props have changed
    return (
      prevProps.sortColumn === nextProps.sortColumn &&
      prevProps.sortDirection === nextProps.sortDirection &&
      prevProps.lastUpdatedPost === nextProps.lastUpdatedPost &&
      prevProps.posts === nextProps.posts // This works because we're using memoized posts
    );
  }
);

PostsTable.displayName = "PostsTable";

// Add this type for post sorting
type PostSortConfig = {
  column: string;
  direction: "asc" | "desc";
};

// Add this interface for campaign stats
interface CampaignStats {
  views: number;
  engagement_rate: number;
  posts: number;
  creators: number;
  historical: Array<{ views: number; date: string }>;
  today: number;
}

// Add this helper function near the top of the file
const getInitial = (name: string) => {
  return name.charAt(0).toUpperCase();
};

// Add these type definitions at the top level
type Platform = "tiktok" | "instagram" | "youtube";

// Define platform options as a constant at the top level
const platformOptions = [
  {
    label: "TikTok",
    value: "tiktok" as Platform,
    icon: <FaTiktok className="h-4 w-4" />,
  },
  {
    label: "Instagram",
    value: "instagram" as Platform,
    icon: <Instagram className="h-4 w-4" />,
  },
  {
    label: "YouTube",
    value: "youtube" as Platform,
    icon: <Youtube className="h-4 w-4" />,
  },
];

// Add this validation schema after the existing type definitions
const updateCampaignSchema = z.object({
  name: z.string().min(1, "Campaign name is required"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  creatorInstructions: z.string().optional(),
  assetsLink: z
    .string()
    .url("Please enter a valid URL")
    .or(z.string().length(0)),
  platforms: z.array(z.string()).min(1, "Select at least one platform"),
  startType: z.enum(["now", "later"]),
  startDate: z.date().optional(),
  startTime: z.string().optional(),
  endType: z.enum(["never", "scheduled"]),
  endDate: z.date().optional(),
  endTime: z.string().optional(),
  timezone: z.string(),
  budgetType: z.enum(["none", "per_post", "cpm"]),
  budget: z.coerce
    .number()
    .min(0, "Budget must be greater than 0")
    .optional()
    .nullable(),
  earningsPerPost: z.coerce
    .number()
    .min(0, "Earnings per post must be greater than 0")
    .optional()
    .nullable(),
  baseCpm: z.coerce
    .number()
    .min(0, "Base CPM must be greater than 0")
    .optional()
    .nullable(),
  locationCpms: z
    .array(
      z.object({
        id: z.string().optional(), // Make id optional since it might not exist initially
        type: z.enum(["continent", "country"]),
        location: z.string(),
        cpm: z
          .string()
          .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
            message: "CPM must be a positive number",
          }),
      })
    )
    .optional()
    .nullable()
    .transform((val) => (!val ? [] : val)), // Transform null/undefined to empty array
});

// Add these constants after the existing type definitions
const generateId = () => nanoid();

const continentOptions = [
  { value: "europe", label: "Europe" },
  { value: "north_america", label: "North America" },
  { value: "south_america", label: "South America" },
  { value: "asia", label: "Asia" },
  { value: "africa", label: "Africa" },
  { value: "oceania", label: "Oceania" },
];

const countryOptions = Object.entries(countries).map(([code, country]) => ({
  value: code.toLowerCase(),
  label: country.name,
}));

// First, let's add these interfaces near the top of the file where other interfaces are defined
interface Creator {
  id: number;
  country: string;
  cpm: string | number | null; // Update this line
  user: {
    name: string;
    avatar: string;
    email: string;
    is_onboarded: number;
  };
  accounts: Array<{
    id: number;
    platform: string;
    username: string;
    display_name: string;
    avatar: string;
    followers: number;
    url: string;
  }>;
}

// Add this interface near your other interfaces
interface Invitation {
  id: number;
  uuid: string;
  email: string;
  user: {
    name: string;
    avatar: string;
    email: string;
  } | null;
  invited_by: {
    name: string;
    avatar: string;
  };
}

interface ShareCampaignContentProps {
  uuid: string;
  initialData?: {
    campaign: Campaign;
    posts: Post[];
    stats: {
      historical: Array<{ views: number; date: string }>;
      today: number;
    };
  };
}

export default function CampaignOverviewPage() {
  const { setCrumbs } = useBreadcrumbs();
  const params = useParams();
  const campaignId = params.uuid as string;
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewStats, setViewStats] = useState<{
    historical: Array<{ views: number; date: string }>;
    today: number;
  } | null>(null);
  const { toast } = useToast();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);

  // Add these states back but simplified
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Replace separate sort states with a single sort config
  const [sortConfig, setSortConfig] = useState<PostSortConfig>({
    column: "posted_at",
    direction: "desc",
  });

  // Move the platform filters state inside the component
  const [platformFilters, setPlatformFilters] = useState<Platform[]>([]);

  // Add this state for CSV export
  const [isExporting, setIsExporting] = useState(false);

  // Add this state for the active settings section
  const [activeSection, setActiveSection] = useState<
    "general" | "requirements" | "platforms" | "scheduling" | "budget"
  >("general");

  // First, add this state near the postUrl state
  const [countTowardsBudget, setCountTowardsBudget] = useState(false);

  // Add these states after other state declarations
  const [creatorSearchQuery, setCreatorSearchQuery] = useState("");
  const [creatorStats, setCreatorStats] = useState<
    Record<
      number,
      {
        posts: number;
        views: number;
        engagement_rate: number;
        earnings: number;
      }
    >
  >({});

  // Add this memoized filtered creators
  const filteredCreators = useMemo(() => {
    return (campaign?.creators || []).filter((creator) => {
      const searchLower = creatorSearchQuery.toLowerCase();
      return (
        creator.user.name.toLowerCase().includes(searchLower) ||
        creator.user.email.toLowerCase().includes(searchLower) ||
        creator.accounts.some(
          (account) =>
            account.username.toLowerCase().includes(searchLower) ||
            account.display_name.toLowerCase().includes(searchLower)
        )
      );
    });
  }, [campaign?.creators, creatorSearchQuery]);

  // Add this type for creator sorting
  type CreatorSortConfig = {
    column:
      | "followers"
      | "posts"
      | "views"
      | "engagement"
      | "cpm"
      | "earnings"
      | null;
    direction: "asc" | "desc";
  };

  // Add this state near your other states
  const [creatorSortConfig, setCreatorSortConfig] = useState<CreatorSortConfig>(
    {
      column: null,
      direction: "desc",
    }
  );

  // Add this memoized sorted creators after the filtered creators
  const sortedCreators = useMemo(() => {
    const creators = [...filteredCreators];

    if (!creatorSortConfig.column) return creators;

    return creators.sort((a, b) => {
      const direction = creatorSortConfig.direction === "asc" ? 1 : -1;

      switch (creatorSortConfig.column) {
        case "followers":
          return (
            (a.accounts.reduce((sum, acc) => sum + acc.followers, 0) -
              b.accounts.reduce((sum, acc) => sum + acc.followers, 0)) *
            direction
          );
        case "posts":
          return (
            ((creatorStats[a.id]?.posts || 0) -
              (creatorStats[b.id]?.posts || 0)) *
            direction
          );
        case "views":
          return (
            ((creatorStats[a.id]?.views || 0) -
              (creatorStats[b.id]?.views || 0)) *
            direction
          );
        case "engagement":
          return (
            ((creatorStats[a.id]?.engagement_rate || 0) -
              (creatorStats[b.id]?.engagement_rate || 0)) *
            direction
          );
        case "cpm":
          const aCpm = a.cpm
            ? typeof a.cpm === "string"
              ? parseFloat(a.cpm)
              : a.cpm
            : 0;
          const bCpm = b.cpm
            ? typeof b.cpm === "string"
              ? parseFloat(b.cpm)
              : b.cpm
            : 0;
          return (aCpm - bCpm) * direction;
        case "earnings":
          return (
            ((creatorStats[a.id]?.earnings || 0) -
              (creatorStats[b.id]?.earnings || 0)) *
            direction
          );
        default:
          return 0;
      }
    });
  }, [filteredCreators, creatorSortConfig, creatorStats]);

  // Add this handler
  const handleCreatorSort = (column: CreatorSortConfig["column"]) => {
    setCreatorSortConfig((current) => ({
      column,
      direction:
        current.column === column && current.direction === "desc"
          ? "asc"
          : "desc",
    }));
  };

  // Add these handlers
  const handleEditCreator = (creator: Creator) => {
    setSelectedCreator(creator);
    setCustomCpm(creator.cpm?.toString() || "");
    setShowCpmDialog(true);
  };

  const handleRemoveCreator = (creatorId: number) => {
    // TODO: Implement remove creator
  };

  // Add this effect to calculate creator stats
  useEffect(() => {
    if (!posts.length) return;

    const stats: Record<
      number,
      {
        posts: number;
        views: number;
        engagement_rate: number;
        earnings: number;
      }
    > = {};

    posts.forEach((post) => {
      if (!post.creator?.id) return;

      if (!stats[post.creator.id]) {
        stats[post.creator.id] = {
          posts: 0,
          views: 0,
          engagement_rate: 0,
          earnings: 0,
        };
      }

      stats[post.creator.id].posts += 1;
      stats[post.creator.id].views += post.views;
      stats[post.creator.id].earnings += post.earnings || 0;

      // Calculate weighted engagement rate
      const currentTotal =
        stats[post.creator.id].engagement_rate *
        (stats[post.creator.id].posts - 1);
      const newRate = parseFloat(post.engagement_rate);
      stats[post.creator.id].engagement_rate =
        (currentTotal + newRate) / stats[post.creator.id].posts;
    });

    setCreatorStats(stats);
  }, [posts]);

  // Update the handleExportCSV function
  const handleExportCSV = useCallback(async () => {
    try {
      setIsExporting(true);

      const campaignService = CampaignService.getInstance();
      const blob = await campaignService.exportPosts(campaignId);

      // Create a filename with timestamp
      const filename = `campaign-posts-${
        new Date().toISOString().split("T")[0]
      }.csv`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export complete",
        description: "Your CSV file has been downloaded",
      });
    } catch (error: any) {
      toast({
        title: "Export failed",
        description: error.message || "Failed to export data",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  }, [campaignId, toast]);

  // Update the memoized processedPosts to include account name and handle in search
  const processedPosts = useMemo(() => {
    // First filter
    const filtered = posts.filter((post) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        post.caption.toLowerCase().includes(searchLower) ||
        post.account.name.toLowerCase().includes(searchLower) ||
        post.account.handle.toLowerCase().includes(searchLower);

      const matchesPlatform =
        platformFilters.length === 0 ||
        platformFilters.includes(post.platform as Platform);

      return matchesSearch && matchesPlatform;
    });

    // Then sort (modify array in place to maintain reference)
    return filtered.sort((a, b) => {
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      switch (sortConfig.column) {
        case "views":
          return (a.views - b.views) * direction;
        case "creator":
          return (
            (a.creator?.name || "").localeCompare(b.creator?.name || "") *
            direction
          );
        case "account":
          return a.account.name.localeCompare(b.account.name) * direction;
        case "engagement_rate":
          return (
            (parseFloat(a.engagement_rate) - parseFloat(b.engagement_rate)) *
            direction
          );
        case "posted_at":
          return (
            (new Date(a.posted_at).getTime() -
              new Date(b.posted_at).getTime()) *
            direction
          );
        case "earnings":
          if (a.earnings === null && b.earnings === null) return 0;
          if (a.earnings === null) return 1;
          if (b.earnings === null) return -1;
          return (a.earnings - b.earnings) * direction;
        default:
          return 0;
      }
    });
  }, [posts, searchQuery, sortConfig, platformFilters]);

  // Memoize the paginated posts
  const paginatedPosts = useMemo(
    () =>
      processedPosts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [processedPosts, currentPage, itemsPerPage]
  );

  // Add this memoized pageCount calculation
  const pageCount = useMemo(
    () => Math.ceil(processedPosts.length / itemsPerPage),
    [processedPosts.length, itemsPerPage]
  );

  // Update the sort handler
  const handleSortChange = useCallback((column: string) => {
    setSortConfig((current) => ({
      column,
      direction:
        current.column === column && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  }, []);

  // Update the document title when campaign data loads
  useEffect(() => {
    if (campaign) {
      document.title = `${campaign.name} | GetViraly`;
    }
  }, [campaign]);

  // Add campaign fetching effect
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setIsLoading(true);
        const campaignService = CampaignService.getInstance();
        const data = await campaignService.getSharedCampaign(campaignId);
        setCampaign(data.campaign);
        setPosts(data.posts);
        setViewStats(data.stats);

        setIsLoadingPosts(false);

        // Update breadcrumbs with real campaign name
        setCrumbs([
          { href: "/", label: "Home" },
          { href: "/campaigns", label: "Campaigns" },
          { label: data.campaign.name },
        ]);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to fetch campaign",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId, setCrumbs, toast]);

  const PostsTableSkeleton = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[300px]">Post</TableHead>
          <TableHead className="w-[100px]">Platform</TableHead>
          <TableHead className="w-[200px]">Creator</TableHead>
          <TableHead>Views</TableHead>
          <TableHead>Engagement</TableHead>
          <TableHead>Interactions</TableHead>
          <TableHead>Posted</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...Array(5)].map((_, index) => (
          <TableRow key={index}>
            <TableCell>
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-16" />
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-16" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-16" />
            </TableCell>
            <TableCell>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            </TableCell>
            <TableCell>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-20" />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  // Add state to track the last updated post
  const [lastUpdatedPost, setLastUpdatedPost] = useState<string | null>(null);

  // Update the Pusher effect
  useEffect(() => {
    if (!campaignId) return;

    const channel = echo.private(`campaigns.${campaignId}`);

    const handlePostUpdate = (data: { post: Post }) => {
      setPosts((currentPosts) =>
        currentPosts.map((post) =>
          post.uuid === data.post.uuid ? data.post : post
        )
      );
    };

    const handlePostCreated = (data: { post: Post }) => {
      // Add isNew flag to the new post
      const newPost = { ...data.post, isNew: true };
      setPosts((currentPosts) => [newPost, ...currentPosts]);

      // Remove the isNew flag after animation
      setTimeout(() => {
        setPosts((currentPosts) =>
          currentPosts.map((post) =>
            post.uuid === data.post.uuid ? { ...post, isNew: false } : post
          )
        );
      }, 1000);

      setLastUpdatedPost(data.post.uuid);
      setTimeout(() => setLastUpdatedPost(null), 700);

      toast({
        title: "New post added",
        description: "A new post has been added to the campaign",
      });
    };

    channel.listen(".campaignPostUpdated", handlePostUpdate);
    channel.listen(".campaignPostCreated", handlePostCreated);

    return () => {
      channel.stopListening(".campaignPostUpdated");
      channel.stopListening(".campaignPostCreated");
      echo.leaveChannel(`campaigns.${campaignId}`);
    };
  }, [campaignId, toast]);

  // Add state for remove post dialog
  const [showRemovePostDialog, setShowRemovePostDialog] = useState(false);
  const [postToRemove, setPostToRemove] = useState<string | null>(null);
  const [isRemovingPost, setIsRemovingPost] = useState(false);

  // Add this effect to listen for sync progress
  useEffect(() => {
    if (!campaignId) return;

    const channel = echo.private(`campaigns.${campaignId}`);

    const handleSyncProgress = (data: { total: number; completed: number }) => {
      setCampaign((current) => {
        if (!current) return current;

        // If sync just completed, show toast
        if (data.total === data.completed) {
          toast({
            title: "Sync completed",
            description: `Successfully synced ${data.total} posts`,
          });
        }

        return {
          ...current,
          sync:
            data.total === data.completed
              ? false
              : {
                  total: data.total,
                  completed: data.completed,
                },
        };
      });
    };

    channel.listen(".campaignSyncProgress", handleSyncProgress);

    return () => {
      channel.stopListening(".campaignSyncProgress");
    };
  }, [campaignId, toast]);

  // Add state for campaign stats
  const [stats, setStats] = useState<CampaignStats | null>(null);

  // Add effect to initialize stats from campaign data
  useEffect(() => {
    if (campaign) {
      setStats({
        views: campaign.views,
        engagement_rate: campaign.engagement_rate,
        posts: campaign.posts,
        creators: campaign.creators,
        accounts: campaign.accounts_count,
        historical: [],
        today: 0,
      });
    }
  }, [campaign]);

  // Add the stats update listener
  useEffect(() => {
    if (!campaignId) return;

    const channel = echo.private(`campaigns.${campaignId}`);

    const handleStatsUpdate = (data: CampaignStats) => {
      setStats(data);
    };

    channel.listen(".campaignStatsUpdated", handleStatsUpdate);

    return () => {
      channel.stopListening(".campaignStatsUpdated");
    };
  }, [campaignId]);

  // Add this state for the start campaign dialog
  const [showStartCampaignDialog, setShowStartCampaignDialog] = useState(false);
  const [isStartingCampaign, setIsStartingCampaign] = useState(false);

  // Add this handler for starting the campaign
  const handleStartCampaign = async () => {
    try {
      setIsStartingCampaign(true);
      // TODO: Implement API call
      toast({
        title: "Campaign started",
        description: "All creators have been notified",
      });
      setShowStartCampaignDialog(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start campaign",
        variant: "destructive",
      });
    } finally {
      setIsStartingCampaign(false);
    }
  };

  // Add this form logic before the return statement
  const form = useForm<z.infer<typeof updateCampaignSchema>>({
    resolver: zodResolver(updateCampaignSchema),
    defaultValues: {
      name: campaign?.name || "",
      description: campaign?.description || "",
      creatorInstructions: campaign?.creator_instructions || "",
      assetsLink: campaign?.assets_link || "",
      platforms: campaign?.platforms || [],
      startType: campaign?.starts_at ? "later" : "now",
      startDate: campaign?.starts_at ? new Date(campaign.starts_at) : undefined,
      startTime: campaign?.starts_at
        ? new Date(campaign.starts_at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
        : "00:00",
      endType: campaign?.ends_at ? "scheduled" : "never",
      endDate: campaign?.ends_at ? new Date(campaign.ends_at) : undefined,
      endTime: campaign?.ends_at
        ? new Date(campaign.ends_at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
        : "00:00",
      timezone:
        campaign?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      budgetType: campaign?.budget_type || "none",
      budget: campaign?.budget || 0,
      earningsPerPost: campaign?.earnings_per_post || 0,
      baseCpm: campaign?.base_cpm || 0,
      locationCpms: campaign?.location_cpms || [],
    },
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // Inside the CampaignOverviewPage component, add this handler
  const handleShare = useCallback(() => {
    const shareUrl = `${window.location.origin}/share/${campaignId}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Share link copied",
      description: "The campaign link has been copied to your clipboard",
    });
  }, [campaignId, toast]);

  if (isLoading) {
    return (
      <div className="flex-1 space-y-4 p-8 pt-6">
        <div className="space-y-3">
          <Skeleton className="h-8 w-[200px]" />
          <Skeleton className="h-4 w-[300px]" />
        </div>
        {/* Add more skeleton UI as needed */}
      </div>
    );
  }

  if (!campaign) return null;

  // First, add this CSS to handle the active state of sidebar buttons
  const sidebarButtonClasses =
    "w-full justify-start gap-2 relative hover:bg-muted/50";
  const activeButtonClasses = "bg-muted font-medium"; // Removed the border styling

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold tracking-tight">{campaign.name}</h2>
          <p className="text-muted-foreground">{campaign.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShare}
            className="h-8 w-8"
            title="Copy share link"
          >
            <Share className="h-4 w-4" />
          </Button>
          <div
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border capitalize ${
              campaign.status === "active"
                ? "bg-green-100 text-green-800 border-green-200"
                : campaign.status === "draft"
                ? "bg-gray-100 text-gray-800 border-gray-200"
                : campaign.status === "completed"
                ? "bg-blue-100 text-blue-800 border-blue-200"
                : "bg-yellow-100 text-yellow-800 border-yellow-200"
            }`}
          >
            {campaign.status === "pending" ? "Scheduled" : campaign.status}
          </div>
          <HoverCard openDelay={0} closeDelay={0}>
            <HoverCardTrigger>
              {campaign.sync ? (
                <SyncProgress
                  value={(campaign.sync.completed / campaign.sync.total) * 100}
                />
              ) : null}
            </HoverCardTrigger>
          </HoverCard>
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <CalendarClock className="h-4 w-4" />
          Begins {new Date(campaign.starts_at).toLocaleString()}
        </div>
        {campaign.ends_at && (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Ends {new Date(campaign.ends_at).toLocaleString()}
          </div>
        )}
        {campaign.ended_at && (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Ended {new Date(campaign.ended_at).toLocaleString()}
          </div>
        )}
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4" />
          <span>Manager</span>
          <div className="relative h-5 w-5 rounded-full overflow-hidden">
            <Image
              src={campaign.manager.avatar}
              alt={campaign.manager.name}
              fill
              className="object-cover"
            />
          </div>
          <span>{campaign.manager.name}</span>
        </div>
        {campaign.budget_type !== "none" && campaign.budget && (
          <>
            <div className="flex items-center gap-1">
              <DollarSign className="h-4 w-4" />
              {campaign.budget_type === "cpm" ? "CPM Based" : "Per Post"}
            </div>
            <div className="flex items-center gap-2">
              <span>
                €{campaign.used_budget.toLocaleString()}
                <span className="text-muted-foreground/75">
                  /€{campaign.budget.toLocaleString()}
                </span>
              </span>
              <div className="w-24 h-1.5">
                <Progress
                  value={(campaign.used_budget / campaign.budget) * 100}
                  className="h-1.5"
                />
              </div>
              <span className="text-xs">
                {((campaign.used_budget / campaign.budget) * 100).toFixed(1)}%
              </span>
            </div>
          </>
        )}
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="posts">Posts</TabsTrigger>
          <TabsTrigger value="creators" className="hidden">
            Creators
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Total Views
                  </CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stats ? (
                      <AnimatedValue
                        value={stats.views}
                        postUuid="stats"
                        field="views"
                      />
                    ) : (
                      "—"
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Engagement Rate
                  </CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold flex items-center">
                    {stats ? (
                      <>
                        <AnimatedValue
                          value={stats.engagement_rate}
                          postUuid="stats"
                          field="engagement_rate"
                        />
                        <span className="ml-0.5">%</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Total Posts
                  </CardTitle>
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stats ? (
                      <AnimatedValue
                        value={stats.posts}
                        postUuid="stats"
                        field="posts"
                      />
                    ) : (
                      "—"
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Accounts
                  </CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stats ? (
                      <AnimatedValue
                        value={stats.accounts}
                        postUuid="stats"
                        field="creators"
                      />
                    ) : (
                      "—"
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
              <Card className="col-span-4">
                <CardHeader>
                  <CardTitle>Daily Views</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Detailed view statistics for your campaign
                  </p>
                </CardHeader>
                <CardContent>
                  <ViewsTable
                    status={campaign.status}
                    hasPosts={posts.length > 0}
                    data={viewStats}
                  />
                </CardContent>
              </Card>

              <RecentPosts posts={posts} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="posts">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-8 w-[150px] lg:w-[250px]"
                />
                <DataTableFacetedFilter
                  title="Platform"
                  options={platformOptions}
                  value={platformFilters}
                  onChange={setPlatformFilters}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  className="h-8"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Export CSV
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="rounded-md border">
              {isLoadingPosts ? (
                <PostsTableSkeleton />
              ) : processedPosts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] p-8 text-center">
                  <Frown className="h-8 w-8 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No posts yet</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    There are currently no posts submitted to this campaign.
                  </p>
                </div>
              ) : (
                <>
                  <PostsTable
                    posts={paginatedPosts}
                    sortColumn={sortConfig.column}
                    sortDirection={sortConfig.direction}
                    onSortChange={handleSortChange}
                    lastUpdatedPost={lastUpdatedPost}
                    onRemovePost={() => {}}
                    onSyncPost={() => {}}
                    isAdmin={false}
                  />
                  <div className="flex items-center justify-end space-x-2 py-4 px-4 border-t">
                    <div className="flex-1 text-sm text-muted-foreground">
                      {processedPosts.length} posts
                    </div>
                    <div className="space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === pageCount}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Add the DataTableFacetedFilter component
function DataTableFacetedFilter({
  title,
  options,
  value,
  onChange,
}: {
  title: string;
  options: { label: string; value: string; icon?: React.ReactNode }[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircle className="mr-1 h-4 w-4" />
          {title}
          {value.length > 0 && (
            <>
              <div className="mx-2 h-4 w-[1px] bg-muted-foreground/20" />
              <div className="flex gap-1">
                {value.map((selectedValue) => (
                  <span
                    key={selectedValue}
                    className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium leading-none"
                  >
                    {options.find((opt) => opt.value === selectedValue)?.label}
                  </span>
                ))}
              </div>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={value.includes(option.value)}
            onCheckedChange={(checked) => {
              onChange(
                checked
                  ? [...value, option.value]
                  : value.filter((v) => v !== option.value)
              );
            }}
          >
            {option.icon && <span className="mr-2">{option.icon}</span>}
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
        {value.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-center"
              onClick={() => onChange([])}
            >
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Add this new component
const ViewsTable = ({
  status,
  hasPosts,
  data,
}: {
  status: string;
  data: any;
  hasPosts: boolean;
}) => {
  const [viewStats, setViewStats] = useState<{
    historical: Array<{ views: number; date: string }>;
    today: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setViewStats(data);
    setIsLoading(false);
  }, [data]);

  if (status === "pending") {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] p-8 text-center">
        <Clock className="h-8 w-8 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">Campaign Not Started</h3>
        <p className="text-sm text-muted-foreground mt-1">
          View statistics will be available once the campaign is active.
        </p>
      </div>
    );
  }

  if (!hasPosts) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] p-8 text-center">
        <FileX className="h-8 w-8 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">No Posts Yet</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Statistics will be available once posts are added to the campaign.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!viewStats) return null;

  // Prepare data for the chart
  const chartData = [
    ...viewStats.historical.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    ),
    { date: format(new Date(), "yyyy-MM-dd"), views: viewStats.today },
  ].map((item) => ({
    date: format(parseISO(item.date), "MMM d"),
    views: item.views,
  }));

  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 20 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            className="text-sm text-muted-foreground"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            className="text-sm text-muted-foreground"
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="rounded-lg border bg-background p-2 shadow-sm">
                    <div className="space-y-1">
                      <p className="text-[0.70rem] uppercase text-muted-foreground">
                        {label}
                      </p>
                      <div className="font-bold">
                        {payload[0].value.toLocaleString()} views
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar
            dataKey="views"
            fill="currentColor"
            className="fill-primary"
            radius={[4, 4, 0, 0]}
            label={{
              position: "top",
              content: (props) => {
                const { value } = props;
                return (
                  <text
                    x={props.x + props.width / 2}
                    y={props.y - 10}
                    textAnchor="middle"
                    className="text-xs fill-muted-foreground"
                  >
                    {formatViews(value as number)}
                  </text>
                );
              },
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Add this skeleton component
const CreatorsTableSkeleton = () => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="w-[250px]">Creator</TableHead>
        <TableHead>Country</TableHead>
        <TableHead>Social Accounts</TableHead>
        <TableHead>Total Followers</TableHead>
        <TableHead>Posts</TableHead>
        <TableHead>Views</TableHead>
        <TableHead>Engagement</TableHead>
        <TableHead>Earnings</TableHead>
        <TableHead>Custom CPM</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-12" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-6 w-16 rounded-full" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

// Add this component for the invitations section
